import { readCookie } from '@/components/table/DataTable/util';
import { getUrlWithHost } from '@/helpers/url';
import { generateRequestId, normalizeHost } from '@/helpers/utilities';
import { AccountNotifications, SaveAmbassadorType, SaveNotificationsResponse, SaveNotificationsType } from '@/models/accountModels';
import {
  AccountActivation,
  AccountActivationResponse,
  ActivitiesResponse,
  AmbassadorProfile,
  AuthUser,
  ChangePasswordResponse,
  RegisterFormData,
  RegisterResponse,
  Role,
  SaveAmbassadorResponse,
  UserAccount,
} from '@/models/authModels';
import { Channel } from '@/models/channelModels';
import { CompanyAccount } from '@/models/companyModels';
import { AmbassadorError, DispatchEmptyObject, DispatchObject } from '@/models/globalModels';
import ApiService from '@/services/ApiService';
import LocalDataService from '@/services/LocalDataService';
import { getPortalJsonByUid } from '@/util/getPortalJson';

import { loadAmbassadorCampaigns } from '../../campaigns/actions';
import { doCustomization } from '../../customizations/actions';
import { RootState } from '../../rootReducer';
import * as t from '../types';

export const doLogin = (email: string, password: string, host: string) => (dispatch: any) => {
  dispatch(doLoginFetching());
  ApiService.doLogin(email, password, host)
    .then((userAuth) => {
      const companyId = LocalDataService.getCompanyId();
      const token = userAuth.token;
      const userId = userAuth.account.userid;
      LocalDataService.saveAuth(token);
      LocalDataService.saveUserId(userId);
      LocalDataService.setUserSessionData(userAuth);
      dispatch(doLoginFetch(userAuth));
      dispatch(doUserFetching());
      return getUserData(companyId, userId, token);
    })
    .then((userAccount) => {
      const uid = userAccount.uid;
      LocalDataService.saveUid(uid);
      dispatch(doUserFetch(userAccount));
      dispatch(doAmbassadorFetching());
      return ApiService.getAmbassadorProfile(uid);
    })
    .then((ambassadorProfile) => {
      dispatch(doAmbassadorFetch(ambassadorProfile));
    })
    .catch((e) => {
      console.log('do login error', e);
      dispatch(doLoginFetchError(e)); //intercepts any of the errors in the promise chain
    });
};

export const doRegister = (registerData: RegisterFormData, companyId: number, isAutoLogin: boolean) => (dispatch: any) => {
  dispatch(doRegisterFetching());
  ApiService.createSession()
    .then((session) => {
      const sessionId = session.client_session_uid;
      const requestHeaders = {
        'X-Mbsy-Client-Session-ID': sessionId,
        'X-Mbsy-Client-Request-ID': generateRequestId(),
      };
      return ApiService.doRegister(registerData, requestHeaders, companyId);
    })
    .then((response) => {
      // auto login if enabled by company setting customization
      if (response.account && response.token && isAutoLogin) {
        const token = response.token;
        const userId = response.account.userid;
        LocalDataService.saveAuth(token);
        LocalDataService.saveUserId(userId);
        dispatch(doAutoLogin(companyId, userId, token));
      }
      dispatch(doRegisterFetch(response));
    })
    .catch((e) => {
      dispatch(doRegisterFetchError(e)); //intercepts any of the errors in the promise chain
    });
};

// used specifically for register when portal auto login is enabled
const doAutoLogin = (companyId: number, userId: number, token: string) => (dispatch: any) => {
  doUserFetching();
  getUserData(companyId, userId, token)
    .then((userAccount) => {
      const uid = userAccount.uid;
      LocalDataService.saveUid(uid);
      dispatch(doUserFetch(userAccount));
      dispatch(doAmbassadorFetching());
      return ApiService.getAmbassadorProfile(uid);
    })
    .then((ambassadorProfile) => {
      dispatch(doAmbassadorFetch(ambassadorProfile));
    })
    .catch((e) => {
      dispatch(doLoginFetchError(e)); //intercepts any of the errors in the promise chain
    });
};

// gets both auth user and ambassador account data
export const getUserData = (companyId: number, userId: number, token: string) => {
  return new Promise<UserAccount>((resolve, reject) => {
    ApiService.setToken(token);
    Promise.all([ApiService.getUser(userId), ApiService.getAmbassadorFromCompany(companyId)])
      .then(([authUser, user]) => {
        let combinedUser = user;
        combinedUser.access = authUser.access;
        resolve(combinedUser);
      })
      .catch((e) => {
        reject(e);
      });
  });
};

// used to persists company and profile data after user has been authenticated
export const getProfileData = () => async (dispatch: any) => {
  const { auth, host, uid, companyId, userId } = LocalDataService.getAuthObject();

  if (!auth || !host || !uid) {
    clearUserDataAndRedirect();
    return;
  }

  ApiService.setToken(auth);
  // dispatch(doCompanyFetching());
  dispatch(doAmbassadorFetching());
  const authPromises = [
    // ApiService.getCompanyByHost(host),
    ApiService.getAmbassadorProfile(uid),
  ];
  const [/*company,*/ profile] = await Promise.all(
    authPromises.map((p) =>
      p.catch((e) => {
        doAmbassadorFetchError(e);
        clearUserDataAndRedirect();
      }),
    ),
  );
  try {
    const data = await getUserData(companyId, userId, auth);
    dispatch(doAmbassadorFetch(profile as AmbassadorProfile));
    dispatch(doUserFetch(data));
  } catch (e: any) {
    dispatch(doAmbassadorFetchError(e));
  }
};

export const getCompanyAccount = (host: string) => (dispatch: any) => {
  if (host) {
    dispatch(doCompanyFetching());
    ApiService.getCompanyByHost(host)
      .then((company) => {
        LocalDataService.saveCompanyId(company.uid);
        LocalDataService.savePortalHost(host);
        dispatch(doCompanyFetch(company));
      })
      .catch((e) => dispatch(doCompanyFetchError(e)));
  }
};

export const getAccountNotifications = (uid: number) => (dispatch: any) => {
  dispatch(doNotificationsFetching());
  ApiService.getAccountNotifications(uid)
    .then((notifications) => {
      dispatch(doNotificationsFetch(notifications));
    })
    .catch((e) => dispatch(doNotificationsFetchError(e)));
};

export const doChangePassword =
  (password: string, repeat_password: string, current_password: string, userid: number) => (dispatch: any) => {
    dispatch(doChangePasswordFetching());
    const body = { password, repeat_password, current_password };
    const token = LocalDataService.getAuth();
    ApiService.setToken(token);
    ApiService.changePassword(userid, body)
      .then((data) => {
        dispatch(doChangePasswordFetch(data));
      })
      .catch((e) => {
        dispatch(doChangePasswordFetchError(e));
      });
  };

export const doSaveAmbassador =
  (uid: number, data: SaveAmbassadorType, multipart = false) =>
  (dispatch: any) => {
    dispatch(doSaveAmbassadorFetching());
    const body = data;
    const token = LocalDataService.getAuth();
    ApiService.setToken(token);
    ApiService.saveAmbassador(uid, body, multipart)
      .then((data) => {
        dispatch({
          type: t.SAVE_AMBASSADOR_FETCH,
          payload: data,
        });
      })
      .catch(() => {
        dispatch({
          type: t.SAVE_AMBASSADOR_FETCH_ERROR,
        });
      });
  };

export const doSaveAcctNotifications = (uid: number, data: SaveNotificationsType) => (dispatch: any) => {
  dispatch(doSaveNotificationsFetching());
  const body = data;
  const token = LocalDataService.getAuth();
  ApiService.setToken(token);
  ApiService.saveAcctNotifications(uid, body)
    .then((data) => {
      dispatch({
        type: t.SAVE_NOTIFICATIONS_FETCH,
        payload: data,
      });
    })
    .catch(() => {
      dispatch({
        type: t.SAVE_NOTIFICATIONS_FETCH_ERROR,
      });
    });
};

// used for both scenarios: isActive handles activation/deactivation status
export const handleAccountActivation = (uid: number, isActive: boolean) => (dispatch: any) => {
  dispatch(doChangeAcctStatusFetching(isActive));
  const body: AccountActivation = { deactivated_by_self: !isActive };
  const token = LocalDataService.getAuth();
  ApiService.setToken(token);
  ApiService.saveAcctActivationStatus(uid, body)
    .then((data) => {
      dispatch(doChangeAcctStatusFetch(data));
    })
    .catch((e) => {
      dispatch(doChangeAcctStatusFetchError(isActive, e));
    });
};

export const setPortalHost = (host: string) => () => {
  return LocalDataService.savePortalHost(host);
};

function getDomainFromUrl(url: string): string | null {
  try {
    const parsedUrl = new URL(url);

    // Check if the `testHost` query parameter exists
    const testHost = parsedUrl.searchParams.get('testHost');
    if (testHost) {
      return testHost;
    }

    // Otherwise, return the hostname of the current URL
    return parsedUrl.hostname;
  } catch (error) {
    console.error('Invalid URL:', error);
    return null;
  }
}

export const getPortalHost = (): string => {
  return LocalDataService.getPortalHost();
};

export const checkUserRole = (roles: Role[]) => (_: any, getState: () => RootState) => {
  const { user } = getState().auth;
  const isLogged = !(!user || roles.map((r) => Number(r)).indexOf(user.access) != -1);
  return isLogged;
};

const isStorefrontChannel = () => typeof document !== 'undefined' && document.cookie.includes('portal_channel=storefront');

const clearChannelCookie = () => {
  // expire cookie
  try {
    document.cookie = 'portal_channel=; Path=/; Max-Age=0; SameSite=Lax';
  } catch {}
};

const clearUserDataAndRedirect = () => {
  LocalDataService.clearData();

  const pathname = window.location.pathname;

  const isLoginPage = pathname.includes('login');
  const isResetPasswordPage = pathname.includes('set-password');
  const isRegister = pathname.includes('register');
  const isForgot = pathname.includes('forgot');
  const isUnsubscribe = pathname.includes('unsubscribe');
  const isStorefrontPath = pathname.includes('storefront');

  // storefront check
  const fromCookie = readCookie('portal_channel');
  const channel = fromCookie === Channel.STOREFRONT ? Channel.STOREFRONT : Channel.DEFAULT;
  const redirectUrl = channel === Channel.STOREFRONT ? '/storefront/' : '/register/';

  if (isLoginPage || isRegister || isResetPasswordPage || isForgot || isUnsubscribe || isStorefrontPath) return;

  if (isStorefrontChannel()) {
    window.location.href = getUrlWithHost('/storefront', true);
    return;
  }

  clearChannelCookie();
  window.location.href = getUrlWithHost('/register', true);
};

// Storefront login: code (+ optional 2FA fields)
// Returns a Promise so the page can await and redirect cleanly.
export const doStorefrontLogin =
  (payload: { storefront_code: string } & Record<string, string>, hostForCompany?: string) => async (dispatch: any) => {
    dispatch(doLoginFetching());
    try {
      const userAuth = await ApiService.doStorefrontLogin(payload);
      const token = userAuth.token;
      const userId = userAuth.account.userid;

      LocalDataService.saveAuth(token);
      LocalDataService.saveUserId(userId);
      LocalDataService.setUserSessionData(userAuth);
      ApiService.setToken(token);

      let companyId = LocalDataService.getCompanyId();

      // check if companyId is missing
      const isMissingCompanyId = !companyId || Number.isNaN(companyId);

      if (isMissingCompanyId) {
        // client host resolution:
        const resolvedHost =
          normalizeHost(hostForCompany) ||
          normalizeHost(LocalDataService.getPortalHost()) ||
          normalizeHost(readCookie('portal_host')) ||
          normalizeHost(typeof window !== 'undefined' ? window.location.host : undefined);

        if (!resolvedHost) {
          throw new Error('[Storefront] Unable to resolve portal host to fetch company id.');
        }

        const company = await ApiService.getCompanyByHost(resolvedHost);
        if (!company?.uid) {
          throw new Error(`[Storefront] Could not resolve company by host "${resolvedHost}".`);
        }
        companyId = company.uid;
        LocalDataService.saveCompanyId(companyId);
      }

      dispatch(doLoginFetch(userAuth));
      dispatch(doUserFetching());

      const userAccount = await getUserData(companyId as number, userId, token);
      LocalDataService.saveUid(userAccount.uid);
      dispatch(doUserFetch(userAccount));

      dispatch(doAmbassadorFetching());
      const ambassador = await ApiService.getAmbassadorProfile(userAccount.uid);
      dispatch(doAmbassadorFetch(ambassador));
    } catch (e) {
      dispatch(doLoginFetchError(e as AmbassadorError));
      throw e;
    }
  };

// REDUX updates //
export const doLogout = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.LOGOUT,
  });
  clearUserDataAndRedirect();
};

export const doLoginFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.LOGIN_FETCHING,
  });
};

export const doLoginFetch = (user: AuthUser) => (dispatch: DispatchObject<AuthUser>) => {
  dispatch({
    type: t.LOGIN_FETCH,
    payload: user,
  });
};

export const doLoginFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.LOGIN_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doRegisterFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.REGISTER_FETCHING,
  });
};

export const doRegisterFetch = (response: RegisterResponse) => (dispatch: DispatchObject<RegisterResponse>) => {
  dispatch({
    type: t.REGISTER_FETCH,
    payload: response,
  });
};

export const doRegisterFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.REGISTER_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doUserSSOFetch = (user: AuthUser) => (dispatch: any) => {
  dispatch({
    type: t.USER_SSO_FETCH,
    payload: { ...user },
  });
};

export const doUserSSOFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.USER_SSO_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doUserFetch = (user: UserAccount) => (dispatch: any) => {
  dispatch({
    type: t.USER_FETCH,
    payload: { ...user },
  });
};

export const doUserFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.USER_FETCHING,
  });
};

export const doUserFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.USER_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doCompanyFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.COMPANY_FETCHING,
  });
};

export const doCompanyFetch = (company: CompanyAccount) => (dispatch: any) => {
  const costumization = company.customization_uid;
  if (costumization) dispatch(doCustomization(costumization));
  dispatch({
    type: t.COMPANY_FETCH,
    payload: { ...company },
  });
};

export const doCompanyFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.COMPANY_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doAmbassadorFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.AMBASSADOR_FETCHING,
  });
};

export const doAmbassadorFetch = (ambassador: AmbassadorProfile) => (dispatch: any) => {
  const campaignIds = new Set(ambassador.urls.map((u) => u.campaign_id));
  const filteredCampaigns = ambassador.campaigns.filter((c) => !campaignIds.has(c.campaign_id));
  const campaigns = ambassador.urls.length > 0 ? [...ambassador.urls, ...filteredCampaigns] : ambassador.campaigns;

  dispatch({
    type: t.AMBASSADOR_FETCH,
    payload: { ...ambassador },
  });
  dispatch(loadAmbassadorCampaigns(campaigns));
};

export const doAmbassadorFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.AMBASSADOR_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doNotificationsFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.ACCT_NOTIF_FETCHING,
  });
};

export const doNotificationsFetch = (notifications: AccountNotifications) => (dispatch: any) => {
  dispatch({
    type: t.ACCT_NOTIF_FETCH,
    payload: { ...notifications },
  });
};

export const doNotificationsFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.ACCT_NOTIF_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doHydrateMyAccount = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.HYDRATE_MY_ACCT,
  });
};

export const doChangePasswordFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.CHANGE_PASSWORD_FETCHING,
  });
};

export const doChangePasswordFetch = (response: ChangePasswordResponse) => (dispatch: any) => {
  dispatch({
    type: t.CHANGE_PASSWORD_FETCH,
    payload: { ...response },
  });
};

export const doChangePasswordFetchError = (error: AmbassadorError) => (dispatch: any) => {
  dispatch({
    type: t.CHANGE_PASSWORD_FETCH_ERROR,
    payload: { ...error.apiResponse },
  });
};

export const doSaveAmbassadorFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.SAVE_AMBASSADOR_FETCHING,
  });
};

export const doSaveAmbassadorFetch = (response: SaveAmbassadorResponse) => (dispatch: any) => {
  dispatch({
    type: t.SAVE_AMBASSADOR_FETCH,
    payload: { ...response },
  });
};

export const doSaveAmbassadorFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.SAVE_AMBASSADOR_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doSaveNotificationsFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.SAVE_NOTIFICATIONS_FETCHING,
  });
};

export const doSaveNotificationsFetch = (response: SaveNotificationsResponse) => (dispatch: any) => {
  dispatch({
    type: t.SAVE_NOTIFICATIONS_FETCH,
    payload: { ...response },
  });
};

export const doSaveNotificationsFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
  dispatch({
    type: t.SAVE_NOTIFICATIONS_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doChangeAcctStatusFetching = (isActivation: boolean) => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: isActivation ? t.ACTIVATE_ACCOUNT_FETCHING : t.DEACTIVATE_ACCOUNT_FETCHING,
  });
};

export const doChangeAcctStatusFetch = (response: AccountActivationResponse) => (dispatch: any) => {
  const isActivation = !response.deactivated_by_self;
  dispatch({
    type: isActivation ? t.ACTIVATE_ACCOUNT_FETCH : t.DEACTIVATE_ACCOUNT_FETCH,
    payload: { ...response },
  });
};

export const doChangeAcctStatusFetchError =
  (isActivation: boolean, error: AmbassadorError) => (dispatch: DispatchObject<AmbassadorError>) => {
    dispatch({
      type: isActivation ? t.ACTIVATE_ACCOUNT_FETCH_ERROR : t.DEACTIVATE_ACCOUNT_FETCH_ERROR,
      payload: { ...error },
    });
  };

export const doResetLoginStatus = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.RESET_LOGIN_STATUS,
  });
};

export const doForgotPassword = (email: string) => (dispatch: any) => {
  dispatch(doForgotPasswordFetching());
  ApiService.forgotPassword(email)
    .then((data: any) => {
      data['email'] = email;
      dispatch({ type: t.FORGOT_PASSWORD_FETCH, payload: data });
    })
    .catch(() => {
      dispatch({ type: t.FORGOT_PASSWORD_FETCH_ERROR });
    });
};

export const doCleanForgotPassword = () => (dispatch: DispatchEmptyObject) => {
  dispatch({ type: t.FORGOT_PASSWORD_CLEAN });
};

export const doForgotPasswordFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.FORGOT_PASSWORD_FETCHING,
  });
};

export const getRecentActivities = (affiliate_uid: number, uid: number) => (dispatch: any) => {
  dispatch(doActivityFetching());
  ApiService.getActivities(affiliate_uid, uid)
    .then((activites) => {
      dispatch(doActivityFetch(activites));
    })
    .catch((e) => dispatch(doActivityFetchError(e)));
};

export const doActivityFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.ACTIVITY_FETCHING,
  });
};

export const doActivityFetch = (activites: ActivitiesResponse) => (dispatch: any) => {
  dispatch({
    type: t.ACTIVITY_FETCH,
    payload: { ...activites },
  });
};

export const doActivityFetchError = (error: any) => (dispatch: DispatchObject<any>) => {
  dispatch({
    type: t.ACTIVITY_FETCH_ERROR,
    payload: { ...error },
  });
};

export const doResetPassword =
  (email: string, password: string, confirmPassword: string, verificationCode: string, host: string) => (dispatch: any) => {
    dispatch(doResetPasswordFetching());
    ApiService.resetPassword(email, password, confirmPassword, verificationCode, host)
      .then((data) => {
        dispatch({ type: t.RESET_PASSWORD_FETCH, payload: data });
      })
      .catch((e) => {
        dispatch(doResetPasswordFetchError(e));
      });
  };

export const doResetPasswordFetching = () => (dispatch: DispatchEmptyObject) => {
  dispatch({
    type: t.RESET_PASSWORD_FETCHING,
  });
};

export const doResetPasswordFetchError = (error: AmbassadorError) => (dispatch: DispatchObject<any>) => {
  dispatch({
    type: t.RESET_PASSWORD_FETCH_ERROR,
    payload: { ...error.apiResponse },
  });
};

export const doSaveSubscribeSettings =
  (code: string, action: 'none' | 'opt-out' | 'unsubscribe' | 'non-marketing', host: string, emailTemplate?: string) => (dispatch: any) => {
    if (action === 'none') return;
    dispatch(doSaveNotificationsFetching());

    ApiService.saveUnsubscribeSettings(code, action, host, emailTemplate)
      .then((data) => {
        dispatch(doSaveNotificationsFetch(data));
      })
      .catch((e) => {
        dispatch(doSaveNotificationsFetchError(e));
      });
  };

export const setPortalChannel = (channel: Channel) => ({
  type: t.SET_PORTAL_CHANNEL,
  payload: channel,
});

export const getCompanyAccountByUid = (uid: number) => async (dispatch: any) => {
  if (!uid) return;
  dispatch(doCompanyFetching());
  try {
    const company = await getPortalJsonByUid(uid);
    LocalDataService.saveCompanyId(company.uid);
    // IMPORTANT: DO NOT savePortalHost here (generic domain for storefront)
    dispatch(doCompanyFetch(company));
  } catch (e: any) {
    dispatch(doCompanyFetchError(e));
  }
};

// helper to get company by either host or uid
export const getCompanyAccountByRef = (ref: { host?: string; uid?: number }) => async (dispatch: any) => {
  if (!ref?.host && !ref?.uid) return;
  dispatch(doCompanyFetching());
  try {
    const company = await ApiService.getCompanyByRef(ref);
    LocalDataService.saveCompanyId(company.uid);
    if (ref.host) LocalDataService.savePortalHost(ref.host);
    dispatch(doCompanyFetch(company));
  } catch (e) {
    dispatch(doCompanyFetchError(e as AmbassadorError));
  }
};
